import type { FC, ReactNode } from 'react';
import React from 'react';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { N0, N200, N40, N800 } from '@atlaskit/theme/colors';

import { Link } from '@confluence/route-manager';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Card = styled.div({
	position: 'relative',
	cursor: 'pointer',
	border: `1px solid ${token('color.border', N40)}`,
	borderRadius: '8px',
	backgroundColor: token('elevation.surface', N0),
	padding: token('space.200', '16px'),

	'&:hover': {
		boxShadow: token(
			'elevation.shadow.raised',
			'0px 1px 1px rgba(9, 30, 66, 0.25), 0px 0px 1px rgba(9, 30, 66, 0.31)',
		),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InvisibleLink = styled(Link)({
	position: 'absolute',
	border: `1px solid transparent`,
	borderRadius: '8px',
	width: '100%',
	height: '100%',
	top: -1,
	left: -1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Header = styled.div({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginBottom: '14px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardIcon = styled.div({
	marginRight: token('space.075', '6px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardPrimarySecondary = styled.div({
	display: 'flex',
	flexDirection: 'column',
	flexGrow: 1,
	flexShrink: 1,
	minWidth: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardPrimary = styled.div({
	color: token('color.text', N800),
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	font: token('font.heading.xsmall'),
	lineHeight: '16px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardSecondary = styled.div({
	color: token('color.text.subtle', N200),
	font: token('font.body.small'),
	lineHeight: '14px',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	a: {
		color: token('color.text.subtle', N200),
		'&:visited': {
			color: token('color.text.subtle', N200),
		},
		'&:hover': {
			color: token('color.text.subtle', N200),
		},
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardActions = styled.div({
	marginLeft: token('space.050', '4px'),
	flexShrink: 0,
});

type NarrowPageCardLayoutProps = {
	icon?: ReactNode;
	primary: ReactNode;
	secondary?: ReactNode;
	lastModified?: ReactNode;
	actions?: ReactNode;
	className?: string;
	testId?: string;
	url?: string;
	onClick?: () => void;
	ariaLabel?: string;
};

export const NarrowPageCardLayout: FC<NarrowPageCardLayoutProps> = ({
	icon,
	primary,
	secondary,
	lastModified,
	actions,
	className,
	testId,
	url,
	onClick,
	ariaLabel,
}) => {
	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
		<Card className={className} data-testid={testId}>
			<InvisibleLink href={url} onClick={onClick} aria-label={ariaLabel} />
			<ContentWrapper>
				<Header>
					<CardIcon data-testid="list-item-icon">{icon}</CardIcon>
					<CardPrimarySecondary>
						<CardPrimary>{primary}</CardPrimary>
						<CardSecondary>{secondary}</CardSecondary>
					</CardPrimarySecondary>
					{actions && <CardActions>{actions}</CardActions>}
				</Header>
				<CardSecondary>{lastModified}</CardSecondary>
			</ContentWrapper>
		</Card>
	);
};
